import { useMemo } from 'react'
import styles from './buildService.module.css'
import { describeAttributeValue, formatDuration, formatPrice } from '../../../utils/pricing'
import { SKIPPED, isAnswered, isAttributeBlocked } from '../../../utils/serviceBuilder'
import { LuArrowLeft } from "react-icons/lu";

function describeBlockedReason(reasons = []) {
    return reasons.length > 0 ? `Not available with ${reasons.join(', ')}` : 'Not available';
}

function BuildService({
    product,
    attributes = [],
    selections = {},
    blockedByAttribute = {},
    basePrice = 0,
    baseDuration = 0,
    onSelectValue = () => {},
    onBack = () => {},
}) {
    // Each step stays hidden until the one before it has an answer, so choices are made in order.
    const visibleAttributes = useMemo(() => {
        const visible = [];

        for (const attribute of attributes) {
            visible.push(attribute);

            // A fully blocked step has nothing to answer, so it must not stall the reveal.
            if (isAttributeBlocked(attribute, blockedByAttribute[attribute.name])) {
                continue;
            }

            if (!isAnswered(attribute, selections)) {
                break;
            }
        }

        return visible;
    }, [attributes, selections, blockedByAttribute]);

    return (
        <div className={styles.builder}>
            <div className={styles.builderHeaderContainer}>
                <div className={styles.builderHeader}>
                    <button type="button" className={styles.backButton} onClick={onBack}><LuArrowLeft /> Back</button>
                    <h4 className={styles.productName}>{product.name}</h4>
                </div>
                <div className={styles.builderDescription}>
                    <p className={styles.productDescription}>{product.description}</p>
                    <p className={styles.productBase}>
                        Starts at {formatPrice(basePrice)} · {formatDuration(baseDuration)}
                    </p>
                </div>

            </div>


            {attributes.length === 0 ? (
                <p className={styles.emptyOptions}>This service has no options to choose.</p>
            ) : (
                visibleAttributes.map((attribute, attributeIndex) => {
                    const selectedValueID = selections[attribute.name];
                    const isSkipped = isAnswered(attribute, selections) && selectedValueID === SKIPPED;
                    const blockedValues = blockedByAttribute[attribute.name];
                    const allBlocked = isAttributeBlocked(attribute, blockedValues);

                    return (
                        <fieldset key={attribute.attribute_id ?? attribute.name} className={styles.step}>
                            <legend className={styles.stepLegend}>
                                <span className={styles.stepIndex}>{attributeIndex + 1}</span>
                                <span className={styles.stepName}>{attribute.name}</span>
                                <span className={attribute.required ? styles.requiredTag : styles.optionalTag}>
                                    {attribute.required ? 'Required' : 'Optional'}
                                </span>
                            </legend>
                            {allBlocked && (
                                <p className={styles.stepNote}>
                                    No options work with your earlier choices, so this step will be left off.
                                    Change an earlier selection to open it back up.
                                </p>
                            )}
                            <div className={styles.options}>
                                {attribute.values.map((value) => {
                                    const isSelected = selectedValueID === value.id;
                                    const blockedReasons = blockedValues?.get(value.id);
                                    const isBlocked = Boolean(blockedReasons);

                                    return (
                                        <button
                                            type="button"
                                            key={value.id}
                                            aria-pressed={isSelected}
                                            disabled={isBlocked}
                                            title={isBlocked ? describeBlockedReason(blockedReasons) : undefined}
                                            className={`${styles.option}${isSelected ? ` ${styles.optionSelected}` : ''}${isBlocked ? ` ${styles.optionBlocked}` : ''}`}
                                            onClick={() => onSelectValue(attributeIndex, value.id)}
                                        >
                                            <span className={styles.optionValue}>{value.value}</span>
                                            <span className={styles.optionMeta}>
                                                {isBlocked ? describeBlockedReason(blockedReasons) : describeAttributeValue(value)}
                                            </span>
                                        </button>
                                    );
                                })}
                                {!attribute.required && !allBlocked && (
                                    <button
                                        type="button"
                                        aria-pressed={isSkipped}
                                        className={`${styles.option} ${styles.optionSkip}${isSkipped ? ` ${styles.optionSelected}` : ''}`}
                                        onClick={() => onSelectValue(attributeIndex, SKIPPED)}
                                    >
                                        <span className={styles.optionValue}>None</span>
                                        <span className={styles.optionMeta}>Skip this step</span>
                                    </button>
                                )}
                            </div>
                        </fieldset>
                    );
                })
            )}
        </div>
    )
}

export default BuildService
